/**
 * Founders Promo Display Logic
 * 
 * Implements the phase-based scarcity framework from FunnelRules.mdc
 * Never exposes low traction numbers early - focuses on remaining availability
 */

export type FoundersPromoPhase = 'phase1' | 'phase2' | 'phase3' | 'phase4' | 'sold-out';

export interface FoundersPromoDisplay {
  phase: FoundersPromoPhase;
  mainText: string;
  subText: string | null;
  urgencyText: string | null;
  showCounter: boolean;
  showRemaining: boolean;
}

const FOUNDERS_CAP = 100;
const PHASE2_MAX = 70;
const PHASE3_MAX = 95;
const PHASE4_MAX = 99;

/**
 * Determines the phase based on claimed count
 */
export function getFoundersPromoPhase(claimed: number): FoundersPromoPhase {
  if (claimed >= FOUNDERS_CAP) return 'sold-out';
  if (claimed >= PHASE4_MAX) return 'phase4';
  if (claimed >= PHASE3_MAX) return 'phase3';
  if (claimed >= PHASE2_MAX) return 'phase2';
  return 'phase1';
}

/**
 * Gets the display configuration for the founders promo counter
 * Follows FunnelRules.mdc phase-based framework
 */
export function getFoundersPromoDisplay(claimed: number): FoundersPromoDisplay {
  const remaining = Math.max(0, FOUNDERS_CAP - claimed);
  const phase = getFoundersPromoPhase(claimed);

  switch (phase) {
    case 'phase1': {
      // Phase 1 (0-30): No numeric counters, only show the cap
      // Goal: Establish exclusivity without exposing momentum
      return {
        phase: 'phase1',
        mainText: 'Founder Access — Limited to 100 Accounts',
        subText: 'Founders Special (First 100 Only)',
        urgencyText: null,
        showCounter: false,
        showRemaining: false,
      };
    }

    case 'phase2': {
      // Phase 2 (30-70): Keep it vague, no numbers yet
      // Goal: Maintain scarcity without exposing higher remaining counts
      return {
        phase: 'phase2',
        mainText: 'Limited Founder Spots Remaining',
        subText: 'Founder Access Filling Fast',
        urgencyText: null,
        showCounter: false,
        showRemaining: false,
      };
    }

    case 'phase3': {
      // Phase 3 (70-95): Remaining + finality message
      // Goal: Force urgency and decision
      return {
        phase: 'phase3',
        mainText: `Only ${remaining} Founder Spots Left`,
        subText: 'Founder Pricing Closes Forever When These Are Gone',
        urgencyText: `Only ${remaining} spots remaining at this price`,
        showCounter: false,
        showRemaining: true,
      };
    }

    case 'phase4': {
      // Phase 4 (95-99): Optional number + deadline language
      // Goal: Close immediately
      return {
        phase: 'phase4',
        mainText: remaining <= 3 ? 'Final Founder Invites' : `Only ${remaining} Founder Spots Left`,
        subText: 'Founder Pricing Ends Forever When These Are Gone',
        urgencyText: remaining <= 3 ? 'Last Founder Accounts Available' : `${remaining} spots remaining`,
        showCounter: false,
        showRemaining: true,
      };
    }

    case 'sold-out': {
      return {
        phase: 'sold-out',
        mainText: 'Founder Access — SOLD OUT',
        subText: 'All 100 Founder Accounts Have Been Claimed',
        urgencyText: null,
        showCounter: false,
        showRemaining: false,
      };
    }
  }
}

/**
 * Gets compact display text for header/navigation
 * More condensed version for smaller spaces
 */
export function getFoundersPromoCompactDisplay(claimed: number): string {
  const remaining = Math.max(0, FOUNDERS_CAP - claimed);
  const phase = getFoundersPromoPhase(claimed);

  switch (phase) {
    case 'phase1':
      return 'Founders Promo';
    case 'phase2':
      return `${remaining} Left`;
    case 'phase3':
      return `${remaining} Left`;
    case 'phase4':
      return remaining <= 3 ? 'Final Spots' : `${remaining} Left`;
    case 'sold-out':
      return 'Sold Out';
  }
}
